import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { BookOpen, ChevronLeft } from 'lucide-react'
import { publicApi } from '../services/publicApi'
import SiteHeader from './SiteHeader'
import BookCard from './home/BookCard'

const getBookCover = (book) => book?.coverUrl || book?.cover_url || book?.thumbnail

const HERO_FAN4 = [
  { rotate: -10, scale: 0.9, marginLeft: 0, zIndex: 1 },
  { rotate: -4, scale: 1, marginLeft: -34, zIndex: 3 },
  { rotate: 4, scale: 1, marginLeft: -34, zIndex: 3 },
  { rotate: 10, scale: 0.9, marginLeft: -34, zIndex: 1 },
]

const HERO_FAN6 = [
  { rotate: -14, scale: 0.8, marginLeft: 0, zIndex: 1 },
  { rotate: -9, scale: 0.95, marginLeft: -46, zIndex: 2 },
  { rotate: -3, scale: 1.08, marginLeft: -48, zIndex: 4 },
  { rotate: 3, scale: 1.08, marginLeft: -48, zIndex: 4 },
  { rotate: 9, scale: 0.95, marginLeft: -48, zIndex: 2 },
  { rotate: 14, scale: 0.8, marginLeft: -46, zIndex: 1 },
]

const ListHeroCollage = ({ books }) => {
  const booksWithCovers = books.filter((book) => getBookCover(book))
  const fanConfig = booksWithCovers.length >= 6 ? HERO_FAN6 : HERO_FAN4
  const covers = booksWithCovers.slice(0, fanConfig.length)

  if (covers.length === 0) {
    return (
      <div className="flex min-h-[260px] items-center justify-center text-primary-300 lg:min-h-[420px]">
        <BookOpen size={72} strokeWidth={1.5} />
      </div>
    )
  }

  return (
    <div className="relative min-h-[240px] lg:min-h-[340px]">
      <div className="absolute left-[54%] top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-end justify-center px-8 sm:px-12 lg:left-[56%]">
        {covers.map((book, index) => (
          <div
            key={book.id ?? book.item_id ?? `${book.title}-${index}`}
            className="flex aspect-[2/3] shrink-0 items-center justify-center overflow-visible bg-transparent"
            style={{
              width: 'clamp(98px, 12vw, 156px)',
              marginLeft: fanConfig[index].marginLeft,
              transform: `rotate(${fanConfig[index].rotate}deg) scale(${fanConfig[index].scale})`,
              zIndex: fanConfig[index].zIndex,
            }}
          >
            <img
              src={getBookCover(book)}
              alt={book.title || 'Book cover'}
              className="h-full w-full rounded-lg object-contain drop-shadow-[0_18px_22px_rgba(11,99,231,0.18)]"
              loading={index < 6 ? 'eager' : 'lazy'}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

const ListDetail = () => {
  const { id } = useParams()
  const [list, setList] = useState(null)
  const [status, setStatus] = useState('loading') // loading | ready | notfound | error

  useEffect(() => {
    let active = true
    setStatus('loading')
    publicApi
      .getCuratedList(id)
      .then((data) => {
        if (!active) return
        setList(data)
        setStatus('ready')
      })
      .catch((err) => {
        if (!active) return
        setStatus(String(err?.message).includes('404') ? 'notfound' : 'error')
      })
    return () => {
      active = false
    }
  }, [id])

  const items = Array.isArray(list?.items) ? list.items : []

  return (
    <div className="min-h-screen bg-bg-main">
      <SiteHeader />

      <div className="container-custom py-10">
        <Link
          to="/lists"
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary-500 hover:text-primary-600 mb-6"
        >
          <ChevronLeft size={16} />
          Back to lists
        </Link>

        {status === 'loading' && (
          <div>
            <div className="h-8 w-64 bg-bg-soft rounded animate-pulse mb-8" />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="aspect-[2/3] rounded-xl bg-bg-soft animate-pulse" />
              ))}
            </div>
          </div>
        )}

        {status === 'notfound' && (
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold text-text-primary mb-2">List not found</h1>
            <p className="text-text-secondary mb-6">This list may no longer be available.</p>
            <Link to="/" className="btn-primary inline-flex">Back to home</Link>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center py-20">
            <h1 className="text-2xl font-bold text-text-primary mb-2">Something went wrong</h1>
            <p className="text-text-secondary">Please try again in a moment.</p>
          </div>
        )}

        {status === 'ready' && list && (
          <>
            <section className="mb-8 overflow-hidden rounded-lg border border-primary-100 bg-primary-50 shadow-soft">
              <div className="grid lg:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)]">
                <ListHeroCollage books={items} />

                <div className="flex min-h-[260px] flex-col justify-center px-6 py-8 sm:px-10 lg:min-h-[340px] lg:py-10 lg:pl-24 lg:pr-12 xl:pl-28">
                  <div className="mb-5 flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center rounded-full bg-bg-surface px-4 py-2 text-sm font-extrabold uppercase tracking-wide text-primary-700 shadow-soft">
                      By Bukito
                    </span>
                    <span className="inline-flex items-center rounded-full bg-bg-surface px-4 py-2 text-sm font-extrabold text-primary-700 shadow-soft">
                      {items.length} books
                    </span>
                  </div>

                  <h1 className="max-w-2xl text-4xl font-extrabold leading-tight tracking-tight text-text-primary sm:text-5xl lg:text-6xl">
                    {list.name}
                  </h1>

                  {list.description && (
                    <p className="mt-5 max-w-2xl text-lg font-semibold leading-relaxed text-text-secondary sm:text-xl">
                      {list.description}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <div className="mb-4 flex items-center justify-between gap-4">
              <h2 className="text-xl font-extrabold text-text-primary">Books in this list</h2>
              <span className="text-sm font-bold text-text-secondary">{items.length} books</span>
            </div>

            <div className="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {items.map((book) => (
                <BookCard
                  key={book.id ?? book.item_id}
                  book={book}
                  width="w-full"
                  imageFit="contain"
                  coverClassName="bg-transparent shadow-none"
                  titleClassName="mt-4 text-lg sm:text-xl"
                  authorClassName="text-base sm:text-lg"
                  linkState={{ backTo: `/list/${id}` }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ListDetail
